import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { MESSAGE_TYPES } from '@/views/screens/MessageCenter/constants';
import { sum } from 'es-toolkit';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getErrorMessageFromAPI } from '../../utilz';
import { setAppLoader } from '../common/CommonActions';
import {
  deleteMessageInError,
  deleteMessageInSuccess,
  getComposeEmailIdSearchInError,
  getComposeEmailIdSearchInSuccess,
  getMessageCenterFoldersInError,
  getMessageCenterFoldersInSuccess,
  getMessageDetailsInError,
  getMessageDetailsInSuccess,
  getMessageListInError,
  getMessageListInSuccess,
  markAsReadMessageInError,
  markAsReadMessageInSuccess,
  sendMessageInError,
  sendMessageInSuccess,
} from './MessageCenterActions';
import {
  deleteMessageEffect,
  forwardMessageEffect,
  getComposeEmailsEffect,
  getMessageCenterFoldersEffect,
  getMessageCenterMessageDetailsEffect,
  getMessageCenterMessageListEffect,
  replyMessageEffect,
  sendMessageEffect,
  setMessageAsReadEffect,
} from './MessageCenterEffects';
import MessageCenterTypes from './MessageCenterTypes';

function* getFoldersWatcher() {
  try {
    const { data } = yield call(getMessageCenterFoldersEffect);
    yield put(
      getMessageCenterFoldersInSuccess({
        data,
        unreadMessages: data ? sum(data.map(({ unReadMail }) => unReadMail ?? 0)) : 0,
      }),
    );
  } catch (e) {
    yield put(getMessageCenterFoldersInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* getMessagesWatcher({ payload }) {
  try {
    const { data } = yield call(getMessageCenterMessageListEffect, payload);
    yield put(getMessageListInSuccess(data));
  } catch (e) {
    yield put(getMessageListInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* getMessageDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getMessageCenterMessageDetailsEffect, payload);
    yield put(
      getMessageDetailsInSuccess({
        data: data?.length ? data[0] : data,
      }),
    );
  } catch (e) {
    yield put(getMessageDetailsInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* deleteMessageWatcher({ payload }) {
  try {
    yield call(deleteMessageEffect, payload);
    let {
      messageList: { data: messageListData },
    } = yield select((state) => state.messageCenter);
    messageListData = messageListData?.filter(({ msgID }) => msgID === payload.msgID);
    yield put(
      deleteMessageInSuccess({
        msgID: payload.msgID,
        messageListData,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully deleted message.',
    });
  } catch (e) {
    yield put(
      deleteMessageInError({
        message: getErrorMessageFromAPI(e),
        msgID: payload.msgID,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to deleted message.',
    });
  }
}

function* markAsReadWatcher({ payload }) {
  try {
    yield call(setMessageAsReadEffect, payload);
    const {
      activeFolderId,
      folders: { data: foldersData },
      messageList: { data: messageListData },
    } = yield select((state) => state.messageCenter);
    const activeFolderIndex = foldersData?.findIndex((item) => item.folderID === activeFolderId);

    foldersData[activeFolderIndex].unReadMail = foldersData[activeFolderIndex].unReadMail
      ? foldersData[activeFolderIndex].unReadMail - 1
      : 0;
    const messageIndex = messageListData?.findIndex((item) => item.msgId === payload.messageId);
    messageListData[messageIndex].isRead = true;
    yield put(
      markAsReadMessageInSuccess({
        messageId: payload.messageId,
        foldersData,
        messageListData,
      }),
    );
  } catch (e) {
    yield put(markAsReadMessageInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* getComposeEmailWatcher({ payload }) {
  try {
    const { data } = yield call(getComposeEmailsEffect, payload);
    yield put(
      getComposeEmailIdSearchInSuccess({
        ...data,
        data: data.data.map(({ totalRecords, ...row }) => row),
      }),
    );
  } catch (e) {
    yield put(getComposeEmailIdSearchInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* sendMessageWatcher({ payload: { composeType, handleDone, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    if (composeType === MESSAGE_TYPES.REPLY) {
      yield call(replyMessageEffect, payload);
    } else if (composeType === MESSAGE_TYPES.FORWARD) {
      yield call(forwardMessageEffect, payload);
    } else {
      yield call(sendMessageEffect, payload);
    }
    yield put(sendMessageInSuccess());
    if (handleDone) {
      handleDone();
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Message sent.',
    });
  } catch (e) {
    yield put(sendMessageInError({ message: getErrorMessageFromAPI(e) }));
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to send message.',
    });
  }
}

export default function* MessageCenterSaga() {
  yield takeEvery(MessageCenterTypes.GET_FOLDERS_PROGRESS, getFoldersWatcher);
  yield takeEvery(MessageCenterTypes.GET_MESSAGE_LIST_PROGRESS, getMessagesWatcher);
  yield takeEvery(MessageCenterTypes.GET_MESSAGE_DETAILS_PROGRESS, getMessageDetailsWatcher);
  yield takeEvery(MessageCenterTypes.DELETE_MESSAGE_PROGRESS, deleteMessageWatcher);
  yield takeEvery(MessageCenterTypes.MARK_AS_READ_MESSAGE_PROGRESS, markAsReadWatcher);
  yield takeLatest(MessageCenterTypes.GET_COMPOSE_EMAILS_ID_SEARCH_PROGRESS, getComposeEmailWatcher);
  yield takeEvery(MessageCenterTypes.SEND_MESSAGE_PROGRESS, sendMessageWatcher);
}
